import React, { useState, useEffect } from 'react';
import {
  CircleStackIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const DatabaseManagement = () => {
  const [stats, setStats] = useState({});
  const [backupData, setBackupData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  // Current data schema version - increment when structure changes
  const CURRENT_VERSION = '1.0.0';
  const SCHEMA_VERSION_KEY = 'mockData_schemaVersion';

  useEffect(() => {
    loadDatabaseStats();
    checkSchemaVersion();
  }, []);

  // ===================================
  // 📊 DATABASE STATISTICS
  // ===================================
  
  const loadDatabaseStats = () => {
    try {
      // Check authentication before loading data
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      if (!isAuthenticated) {
        setStats({
          farms: 0,
          cycles: 0,
          activities: 0,
          storageSize: '0',
          lastBackup: 'Never',
          version: '1.0.0'
        });
        return;
      }

      const farms = JSON.parse(localStorage.getItem('farms_data') || '[]');
      const cycles = JSON.parse(localStorage.getItem('cycles_data') || '[]');
      const activities = JSON.parse(localStorage.getItem('activities_data') || '[]');
      
      // Calculate storage size
      const totalStorage = Object.keys(localStorage).reduce((total, key) => {
        return total + (localStorage.getItem(key) || '').length;
      }, 0);

      setStats({
        farms: farms.length,
        cycles: cycles.length,
        activities: activities.length,
        storageSize: (totalStorage / 1024).toFixed(2), // KB
        lastBackup: localStorage.getItem('lastBackupDate') || 'Never',
        version: localStorage.getItem(SCHEMA_VERSION_KEY) || '1.0.0'
      });
    } catch (error) {
      console.error('Error loading database stats:', error);
    }
  };

  const checkSchemaVersion = () => {
    const currentSchemaVersion = localStorage.getItem(SCHEMA_VERSION_KEY);
    if (!currentSchemaVersion) {
      localStorage.setItem(SCHEMA_VERSION_KEY, CURRENT_VERSION);
    }
  };

  // ===================================
  // 💾 BACKUP FUNCTIONALITY
  // ===================================
  
  const handleBackup = () => {
    setIsProcessing(true);
    try {
      // Collect all mock data from localStorage
      const backupPayload = {
        version: CURRENT_VERSION,
        timestamp: new Date().toISOString(),
        data: {
          farms_data: JSON.parse(localStorage.getItem('farms_data') || '[]'),
          cycles_data: JSON.parse(localStorage.getItem('cycles_data') || '[]'),
          activities_data: JSON.parse(localStorage.getItem('activities_data') || '[]'),
          farm_stats_data: JSON.parse(localStorage.getItem('farm_stats_data') || '{}'),
          // Add other data tables as needed
          produceTypes: JSON.parse(localStorage.getItem('produceTypes') || '[]'),
          produceCategories: JSON.parse(localStorage.getItem('produceCategories') || '[]'),
          produceSpecifications: JSON.parse(localStorage.getItem('produceSpecifications') || '{}')
        },
        metadata: {
          totalRecords: stats.farms + stats.cycles + stats.activities,
          storageSize: stats.storageSize
        }
      };

      // Create downloadable file
      const blob = new Blob([JSON.stringify(backupPayload, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `smartfarm-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Update last backup date
      localStorage.setItem('lastBackupDate', new Date().toLocaleString());
      
      setMessage({ type: 'success', text: 'Backup created successfully!' });
      loadDatabaseStats();
    } catch (error) {
      console.error('Backup error:', error);
      setMessage({ type: 'error', text: 'Failed to create backup. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  // ===================================
  // 📥 SMART RESTORE FUNCTIONALITY
  // ===================================
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backupData = JSON.parse(e.target.result);
        setBackupData(backupData);
        validateBackupData(backupData);
      } catch (error) {
        setMessage({ type: 'error', text: 'Invalid backup file format.' });
      }
    };
    reader.readAsText(file);
  };

  const validateBackupData = (data) => {
    if (!data.version || !data.data) {
      setMessage({ type: 'error', text: 'Invalid backup file structure.' });
      return;
    }

    const sourceVersion = data.version;
    const targetVersion = CURRENT_VERSION;
    
    if (sourceVersion !== targetVersion) {
      setMessage({ 
        type: 'warning', 
        text: `Backup version (${sourceVersion}) differs from current version (${targetVersion}). Smart migration will be applied.`
      });
    } else {
      setMessage({ type: 'info', text: 'Backup file validated successfully. Ready to restore.' });
    }
  };

  const handleRestore = () => {
    if (!backupData) return;

    setIsProcessing(true);
    try {
      const migratedData = performSmartMigration(backupData.data, backupData.version);
      
      // Apply migrated data to localStorage
      Object.entries(migratedData).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });

      // Update schema version
      localStorage.setItem(SCHEMA_VERSION_KEY, CURRENT_VERSION);
      
      setMessage({ type: 'success', text: 'Data restored successfully with smart migration!' });
      setBackupData(null);
      loadDatabaseStats();
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Restore error:', error);
      setMessage({ type: 'error', text: 'Failed to restore data. Please check the backup file.' });
    } finally {
      setIsProcessing(false);
    }
  };

  // ===================================
  // 🧠 SMART MIGRATION SYSTEM
  // ===================================
  
  const performSmartMigration = (sourceData, sourceVersion) => {
    console.log(`Migrating from version ${sourceVersion} to ${CURRENT_VERSION}`);
    
    // Define current expected schema
    const currentSchema = {
      farms_data: {
        required: ['id', 'name', 'size', 'address', 'postcode', 'city', 'state', 'country'],
        optional: ['coordinates', 'mobileNumber', 'farmerId', 'subscriptionEndDate', 'subscriptionStatus', 'daysRemaining', 'season', 'createdAt', 'updatedAt', 'status']
      },
      cycles_data: {
        required: ['id', 'farmId', 'produceType'],
        optional: ['variant', 'quantity', 'unit', 'managementType', 'seasonStartDate', 'expectedFirstHarvest', 'expectedYield', 'yieldUnit', 'farmingProcess', 'startingStage', 'currentStage', 'progress', 'status', 'createdAt']
      },
      activities_data: {
        required: ['id', 'farmId', 'title', 'scheduledDate'],
        optional: ['cycleId', 'description', 'status', 'type', 'createdAt']
      }
    };

    const migratedData = {};

    // Migrate each data table
    Object.entries(sourceData).forEach(([tableName, tableData]) => {
      if (currentSchema[tableName]) {
        // Table exists in current schema - migrate with field validation
        migratedData[tableName] = migrateTable(tableData, currentSchema[tableName], tableName);
      } else if (Array.isArray(tableData) || typeof tableData === 'object') {
        // Table doesn't exist in current schema but is valid data - keep as is
        migratedData[tableName] = tableData;
      }
    });

    // Add default data for missing tables
    Object.keys(currentSchema).forEach(tableName => {
      if (!migratedData[tableName]) {
        migratedData[tableName] = [];
      }
    });

    return migratedData;
  };

  const migrateTable = (sourceTable, schema, tableName) => {
    if (!Array.isArray(sourceTable)) {
      console.warn(`Table ${tableName} is not an array, keeping as is`);
      return sourceTable;
    }

    return sourceTable.map(record => {
      const migratedRecord = {};
      
      // Copy required fields
      schema.required.forEach(field => {
        if (record.hasOwnProperty(field)) {
          migratedRecord[field] = record[field];
        } else {
          // Provide default values for missing required fields
          migratedRecord[field] = getDefaultValue(field, tableName);
        }
      });

      // Copy optional fields that exist
      schema.optional.forEach(field => {
        if (record.hasOwnProperty(field)) {
          migratedRecord[field] = record[field];
        }
      });

      return migratedRecord;
    });
  };

  const getDefaultValue = (field, tableName) => {
    const defaults = {
      id: () => `${tableName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: 'Unknown',
      title: 'Untitled',
      size: '0 hectares',
      address: 'Not specified',
      postcode: '00000',
      city: 'Unknown',
      state: 'Unknown',
      country: 'Malaysia',
      farmId: 'unknown_farm',
      produceType: 'Unknown',
      scheduledDate: new Date().toISOString().split('T')[0],
      status: 'active',
      createdAt: new Date().toISOString()
    };

    return typeof defaults[field] === 'function' ? defaults[field]() : defaults[field] || '';
  };

  // ===================================
  // 🗑️ CLEAR DATA FUNCTIONALITY
  // ===================================
  
  const handleClearAll = () => {
    setIsProcessing(true);
    try {
      // List of keys to clear (mock data related)
      const mockDataKeys = [
        'farms_data',
        'cycles_data', 
        'activities_data',
        'farm_stats_data',
        'produceTypes',
        'produceCategories',
        'produceSpecifications',
        'lastBackupDate'
      ];

      mockDataKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      // Reset schema version
      localStorage.setItem(SCHEMA_VERSION_KEY, CURRENT_VERSION);
      
      setMessage({ type: 'success', text: 'All mock data cleared successfully!' });
      setShowConfirmClear(false);
      loadDatabaseStats();
    } catch (error) {
      console.error('Clear data error:', error);
      setMessage({ type: 'error', text: 'Failed to clear data. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const getMessageIcon = () => {
    switch (message.type) {
      case 'success': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'info': return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <CircleStackIcon className="h-8 w-8 text-blue-600" />
            <span>Database Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Backup, restore, and manage your mock database</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Cog6ToothIcon className="h-4 w-4" />
          <span>Version {stats.version}</span>
        </div>
      </div>

      {/* Status Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 border border-red-200' :
          message.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          {getMessageIcon()}
          <span className={`${
            message.type === 'success' ? 'text-green-700' :
            message.type === 'error' ? 'text-red-700' :
            message.type === 'warning' ? 'text-yellow-700' :
            'text-blue-700'
          }`}>
            {message.text}
          </span>
        </div>
      )}

      {/* Database Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.farms || 0}</div>
          <div className="text-sm text-gray-500">Farms</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{stats.cycles || 0}</div>
          <div className="text-sm text-gray-500">Production Cycles</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.activities || 0}</div>
          <div className="text-sm text-gray-500">Activities</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.storageSize || 0} KB</div>
          <div className="text-sm text-gray-500">Storage Used</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Section */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <CloudArrowDownIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Backup Database</h2>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Backup:</span>
                <span className="font-medium">{stats.lastBackup}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-600">Total Records:</span>
                <span className="font-medium">{(stats.farms || 0) + (stats.cycles || 0) + (stats.activities || 0)}</span>
              </div>
            </div>
            
            <button
              onClick={handleBackup}
              disabled={isProcessing}
              className={`w-full btn-primary flex items-center justify-center space-x-2 ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <CloudArrowDownIcon className="h-5 w-5" />
              <span>{isProcessing ? 'Creating Backup...' : 'Create Backup'}</span>
            </button>
            
            <div className="text-xs text-gray-500 space-y-1">
              <div>• Exports all farm, cycle, and activity data</div>
              <div>• Includes version information for smart migration</div>
              <div>• Downloads as JSON file to your computer</div>
            </div>
          </div>
        </div>

        {/* Restore Section */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <CloudArrowUpIcon className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Restore Database</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Backup File
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            {backupData && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Backup Version:</span>
                  <span className="font-medium">{backupData.version}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">{new Date(backupData.timestamp).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Total Records:</span>
                  <span className="font-medium">{backupData.metadata?.totalRecords || 'Unknown'}</span>
                </div>
              </div>
            )}
            
            <button
              onClick={handleRestore}
              disabled={!backupData || isProcessing}
              className={`w-full btn-secondary flex items-center justify-center space-x-2 ${
                !backupData || isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <CloudArrowUpIcon className="h-5 w-5" />
              <span>{isProcessing ? 'Restoring...' : 'Restore Data'}</span>
            </button>
            
            <div className="text-xs text-gray-500 space-y-1">
              <div>• Smart migration handles version differences</div>
              <div>• Skips fields that no longer exist</div>
              <div>• Adds default values for new required fields</div>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Data Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <TrashIcon className="h-6 w-6 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Clear All Data</h2>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            <span className="text-red-700 font-medium">Warning: This action cannot be undone!</span>
          </div>
          <p className="text-red-600 text-sm mt-2">
            This will permanently delete all farms, production cycles, activities, and related data.
          </p>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={() => setShowConfirmClear(true)}
            className="btn-outline-red flex items-center space-x-2"
          >
            <TrashIcon className="h-4 w-4" />
            <span>Clear All Data</span>
          </button>
          
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <InformationCircleIcon className="h-4 w-4" />
            <span>Create a backup before clearing data</span>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmClear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Clear All Data</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to clear all database? This will permanently delete:
            </p>
            
            <ul className="text-sm text-gray-600 space-y-1 mb-6">
              <li>• {stats.farms || 0} farms</li>
              <li>• {stats.cycles || 0} production cycles</li>
              <li>• {stats.activities || 0} activities</li>
              <li>• All related statistics and data</li>
            </ul>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmClear(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                disabled={isProcessing}
                className={`flex-1 btn-danger ${
                  isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isProcessing ? 'Clearing...' : 'Clear All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseManagement;
